import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, Filter, FileText } from 'lucide-react';
import {
  reportsApi,
  SpecialPaymentsReportDto,
  ReportFilterDto,
  formatCurrency,
  formatDate,
  handleApiError
} from '../../services/reportsService';
import { getStatusBadgeColor } from '../../services/specialPaymentsService';

const ReportContainer = styled.div`
  padding: 0;
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div<{ $accentColor?: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor || 'var(--primary-color)'};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: var(--border-color);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const CardIcon = styled.div<{ $color?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$color ? `${props.$color}15` : 'var(--primary-light)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color || 'var(--primary-color)'};
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  direction: ltr;
  text-align: right;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const CardSubValue = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  margin-top: 8px;
  direction: ltr;
  text-align: right;
`;

const FiltersContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  padding: 20px;
  margin-bottom: 24px;
`;

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const FiltersIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const FiltersTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const Select = styled.select`
  padding: 10px 14px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  padding: 24px;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const TableIcon = styled.div`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const TableTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
`;

const Th = styled.th`
  background: var(--background-color);
  padding: 16px 20px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--background-color);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px 20px;
  text-align: center;
  font-size: 14px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
`;

const StatusBadge = styled.span<{ $bgColor: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => `${props.$bgColor}15`};
  color: ${props => props.$bgColor};
  filter: saturate(0.7) brightness(0.85);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: var(--text-secondary);
  font-size: 15px;
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  color: var(--error-color);
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: var(--text-secondary);
  font-size: 15px;
`;

interface SpecialPaymentsReportProps {
  filter: ReportFilterDto;
}

const SpecialPaymentsReport: React.FC<SpecialPaymentsReportProps> = ({ filter }) => {
  const navigate = useNavigate();
  const [report, setReport] = useState<SpecialPaymentsReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [selectedClient, setSelectedClient] = useState<number | null>(null);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsApi.getSpecialPaymentsReport(filter);
      setReport(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (clientId: number) => {
    navigate(`/clients/${clientId}/manage/special-payments`);
  };

  if (loading) {
    return <LoadingContainer>טוען דוח תשלומים מיוחדים...</LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (!report) {
    return null;
  }

  // Get unique clients for filter dropdown
  const uniqueClients = Array.from(
    new Map(report.payments.map(p => [p.clientId, { id: p.clientId, name: p.clientName }])).values()
  );

  // Filter payments based on selected filters
  const filteredPayments = report.payments.filter(payment => {
    if (selectedStatus !== null && payment.status !== selectedStatus) {
      return false;
    }
    if (selectedClient !== null && payment.clientId !== selectedClient) {
      return false;
    }
    return true;
  });

  return (
    <ReportContainer>
      {/* Summary Cards */}
      <SummaryCards>
        <SummaryCard $accentColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <Clock size={20} />
            </CardIcon>
            <CardTitle>סה"כ ממתין לתשלום</CardTitle>
          </CardHeader>
          <CardValue>{report.totalPending}</CardValue>
          <CardSubValue>
            סכום: {formatCurrency(report.totalPendingAmount)}
          </CardSubValue>
        </SummaryCard>

        <SummaryCard $accentColor="var(--success-color)">
          <CardHeader>
            <CardIcon $color="var(--success-color)">
              <CheckCircle size={20} />
            </CardIcon>
            <CardTitle>שולם בתקופה</CardTitle>
          </CardHeader>
          <CardValue>{report.paidInPeriod}</CardValue>
          <CardSubValue>
            סכום: {formatCurrency(report.paidInPeriodAmount)}
          </CardSubValue>
        </SummaryCard>

        <SummaryCard $accentColor="var(--error-color)">
          <CardHeader>
            <CardIcon $color="var(--error-color)">
              <XCircle size={20} />
            </CardIcon>
            <CardTitle>בוטלו בתקופה</CardTitle>
          </CardHeader>
          <CardValue>{report.cancelledInPeriod}</CardValue>
          <CardSubValue>תשלומים שבוטלו</CardSubValue>
        </SummaryCard>
      </SummaryCards>

      {/* Filters */}
      <FiltersContainer>
        <FiltersHeader>
          <FiltersIcon>
            <Filter size={16} />
          </FiltersIcon>
          <FiltersTitle>סינון תוצאות</FiltersTitle>
        </FiltersHeader>
        <FiltersRow>
          <FilterGroup>
            <FilterLabel>סטטוס</FilterLabel>
            <Select
              value={selectedStatus ?? ''}
              onChange={(e) => setSelectedStatus(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">הכל</option>
              <option value="1">ממתין לתשלום</option>
              <option value="2">שולם</option>
              <option value="3">בוטל</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>לקוח</FilterLabel>
            <Select
              value={selectedClient ?? ''}
              onChange={(e) => setSelectedClient(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">כל הלקוחות</option>
              {uniqueClients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </FilterGroup>
        </FiltersRow>
      </FiltersContainer>

      {/* Table */}
      <TableContainer>
        <TableHeader>
          <TableIcon>
            <FileText size={18} />
          </TableIcon>
          <TableTitle>פירוט תשלומים ({filteredPayments.length})</TableTitle>
        </TableHeader>
        {filteredPayments.length === 0 ? (
          <EmptyState>לא נמצאו תשלומים מיוחדים בתקופה זו</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>שם לקוח</Th>
                <Th>תיאור</Th>
                <Th>סכום</Th>
                <Th>סטטוס</Th>
                <Th>תאריך תשלום</Th>
                <Th>אמצעי תשלום</Th>
                <Th>נוצר בתאריך</Th>
                <Th>נוצר ע"י</Th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <Tr key={payment.id} onClick={() => handleRowClick(payment.clientId)}>
                  <Td style={{ fontWeight: 600 }}>
                    {payment.clientName}
                  </Td>
                  <Td>{payment.description}</Td>
                  <Td style={{ direction: 'ltr', textAlign: 'center', fontWeight: 600 }}>
                    {formatCurrency(payment.amount)}
                  </Td>
                  <Td>
                    <StatusBadge $bgColor={getStatusBadgeColor(payment.status)}>
                      {payment.statusName}
                    </StatusBadge>
                  </Td>
                  <Td>{formatDate(payment.paymentDate)}</Td>
                  <Td>{payment.paymentMethodName || '-'}</Td>
                  <Td>{formatDate(payment.createdAt)}</Td>
                  <Td>{payment.createdByName}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>
    </ReportContainer>
  );
};

export default SpecialPaymentsReport;
